var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { createTheme } from '@mui/material';
import { getAutocompleteStyles, getAutocompletePaperStyles, } from './autocomplete/autocomplete.styles';
import { BUTTON_SIZES, BUTTON_HIERARCHY_STYLES } from './button/button.styles';
import { isValidButtonSize } from './button/button.types';
import { CHIP_STYLES } from './chip/chip.styles';
import { isValidChipSize, isValidChipType, isValidChipVariant } from './chip/chip.types';
import { COLOR_TOKENS } from './colors/colors';
import { getFormGroupStyles, getFormControlLabelStyles, getRadioStyles, getCheckboxStyles, } from './form/form.styles';
import { getSelectMenuPaperStyles, getMenuItemStyles, getSelectStyles, } from './select/select.styles';
import { SHADOWS } from './shadows/shadows';
import { getSnackbarStyles } from './snackbar/snackbar.styles';
import { CONTAINER_SPACING, RADIUS, SPACING, WIDTH } from './spacing/spacing';
import { getFormHelperTextStyles, getFormLabelAsteriskStyles, getFormLabelStyles, getOutlinedInputInputStyles, getOutlinedInputNotchedOutlineStyles, getOutlinedInputRootStyles, } from './textField/textField.styles';
import { getToolTipStyles, getToolTipArrowStyles } from './tooltip/tooltip.styles';
import { TYPOGRAPHY_VARIANTS, TYPOGRAPHY_VARIANTS_MAPPING } from './typography/typography';
export var theme = createTheme({
    typography: __assign({}, TYPOGRAPHY_VARIANTS),
    backrs: {
        colors: COLOR_TOKENS,
        spacing: SPACING,
        radius: RADIUS,
        width: WIDTH,
        containerSpacing: CONTAINER_SPACING,
        shadows: SHADOWS,
    },
    components: {
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontFamily: '"Sarabun", sans-serif',
                },
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    fontFamily: '"Sarabun", sans-serif',
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginBottom: "".concat(SPACING['spacing-none'], " !important"),
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getFormHelperTextStyles(theme);
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getFormLabelStyles(theme);
                },
                asterisk: function (_a) {
                    var theme = _a.theme;
                    return getFormLabelAsteriskStyles(theme);
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getOutlinedInputRootStyles(theme);
                },
                notchedOutline: function () { return getOutlinedInputNotchedOutlineStyles(); },
                input: function (_a) {
                    var theme = _a.theme;
                    return getOutlinedInputInputStyles(theme);
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: function (_a) {
                    var theme = _a.theme;
                    return getToolTipStyles(theme);
                },
                arrow: function (_a) {
                    var theme = _a.theme;
                    return getToolTipArrowStyles(theme);
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: function (_a) {
                    var _b = _a.ownerState, _c = _b.size, size = _c === void 0 ? 'md' : _c, _d = _b.type, type = _d === void 0 ? 'default' : _d, _e = _b.variant, variant = _e === void 0 ? 'badge' : _e;
                    if (!isValidChipType(type) || !isValidChipSize(size) || !isValidChipVariant(variant)) {
                        return {};
                    }
                    return __assign({}, CHIP_STYLES[size][type][variant]);
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
                disableRipple: true,
            },
            styleOverrides: {
                root: function (_a) {
                    var _b = _a.ownerState, hierarchy = _b.hierarchy, size = _b.size, _c = _b.icon, icon = _c === void 0 ? 'Default' : _c;
                    if (!hierarchy || !isValidButtonSize(size)) {
                        return { boxShadow: 'none' };
                    }
                    return __assign(__assign({}, BUTTON_SIZES[size][icon]), BUTTON_HIERARCHY_STYLES[hierarchy]);
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getSelectStyles(theme);
                },
            },
            defaultProps: {
                MenuProps: {
                    PaperProps: {
                        sx: function (theme) { return getSelectMenuPaperStyles(theme); },
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: function (_a) {
                    var theme = _a.theme;
                    return theme.unstable_sx(getSelectMenuPaperStyles(theme));
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getMenuItemStyles(theme);
                },
            },
        },
        MuiFormGroup: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getFormGroupStyles(theme);
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme, ownerState = _a.ownerState;
                    return getFormControlLabelStyles(theme, ownerState.checked);
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme, ownerState = _a.ownerState;
                    return getRadioStyles(theme, ownerState.checked);
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getCheckboxStyles(theme);
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: TYPOGRAPHY_VARIANTS_MAPPING,
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getAutocompleteStyles(theme);
                },
                paper: function (_a) {
                    var theme = _a.theme;
                    return getAutocompletePaperStyles(theme);
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                root: function (_a) {
                    var theme = _a.theme;
                    return getSnackbarStyles(theme);
                },
            },
        },
    },
});
